/**
 * Live point commands on the map: where each aircraft has been sent, where it
 * is orbiting, and what it has been told to look at.
 *
 * The operator's report was that a GOTO, an orbit or an ROI left nothing on the
 * map at all — the aircraft flew somewhere and the only trace was a line of
 * text in the command panel. This layer draws the coordinate that was actually
 * commanded, per aircraft, in that aircraft's own track colour.
 *
 * Drawn so it can never be read as a planned waypoint. A mission point is a
 * filled disc with a sequence number above it; a command target is a hollow
 * ring around a small core with its verb below it, and an orbit additionally
 * draws its radius on the ground as a dashed circle. "The aircraft is going
 * here now" and "somebody intends to fly here later" are different claims and
 * must not share a shape — the same reasoning that keeps the proposed mission
 * in its own colour in useMissionLayer.
 *
 * A target is drawn only for an aircraft that is on the roster, for the reason
 * recorded in useMissionLayer.present(): an overlay that outlives its aircraft
 * is unselectable, uncancellable and permanent. The store retires targets on
 * arrival and on supersession; this layer is the second gate on the same rule.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { bindHoverLayer } from '@/map/hoverLayer';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { trackColorFor } from '@/map/icons';
import { circleRing } from '@/map/useZoneLayer';
import {
  subscribeToCommandTargets,
  type CommandTarget,
  type CommandTargetKind,
} from '@/stores/commandTargetStore';
import { subscribeToRoster, useDroneStore } from '@/stores/droneStore';

export const COMMAND_TARGET_SOURCE_ID = 'command-targets';
/** Exported: the hover affordance hit-tests this layer, and so do the tests. */
export const COMMAND_TARGET_HALO_LAYER = 'command-targets-halo';
const COMMAND_TARGET_RING_LAYER = 'command-targets-ring';
const COMMAND_TARGET_CORE_LAYER = 'command-targets-core';
const COMMAND_TARGET_LABEL_LAYER = 'command-targets-label';

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

const KIND_LABEL: Record<CommandTargetKind, string> = {
  goto: 'GOTO',
  orbit: 'ORBIT',
  roi: 'ROI',
};

/** The whole target, as a hover label reads it out. */
export interface CommandTargetHover {
  /** Map-container pixel coordinates of the pointer. */
  x: number;
  y: number;
  /** e.g. "ORBIT · REAPER-1". */
  title: string;
  /** Coordinates first, then radius and height where the command carries them. */
  lines: string[];
  color: string;
}

export function commandTargetFeatures(target: CommandTarget): unknown[] {
  const color = trackColorFor(target.droneId);
  const features: unknown[] = [];

  // The radius is half of what an orbit means. A centre dot alone says the
  // aircraft is going to a point when it is in fact circling ground the
  // operator needs to see the extent of.
  if (target.kind === 'orbit' && target.radiusM !== null) {
    features.push({
      type: 'Feature',
      properties: { droneId: target.droneId, kind: target.kind, color },
      geometry: {
        type: 'LineString',
        coordinates: circleRing(target.lat, target.lon, target.radiusM),
      },
    });
  }

  features.push({
    type: 'Feature',
    properties: {
      droneId: target.droneId,
      kind: target.kind,
      color,
      label: KIND_LABEL[target.kind],
      lat: target.lat,
      lon: target.lon,
      radiusM: target.radiusM,
      altM: target.altM,
    },
    geometry: { type: 'Point', coordinates: [target.lon, target.lat] },
  });

  return features;
}

/** The properties MapLibre hands back on a hovered feature. */
interface HoverEvent {
  features?: Array<{ properties: Record<string, unknown> | null } | undefined>;
  point: { x: number; y: number };
}

function kindLabel(kind: string): string {
  return kind === 'goto' || kind === 'orbit' || kind === 'roi'
    ? KIND_LABEL[kind]
    : kind.toUpperCase();
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function hoverFromProperties(
  properties: Record<string, unknown>,
  point: { x: number; y: number },
  callsignFor: (droneId: string) => string,
): CommandTargetHover | null {
  const droneId = properties.droneId;
  const kind = properties.kind;
  if (typeof droneId !== 'string' || typeof kind !== 'string') return null;

  const lat = numberOrNull(properties.lat);
  const lon = numberOrNull(properties.lon);
  const radiusM = numberOrNull(properties.radiusM);
  const altM = numberOrNull(properties.altM);

  const lines: string[] = [];
  if (lat !== null && lon !== null) lines.push(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
  if (radiusM !== null) lines.push(`RADIUS ${Math.round(radiusM)} m`);
  if (altM !== null) lines.push(`ALT ${Math.round(altM)} m AGL`);

  return {
    x: point.x,
    y: point.y,
    // The aircraft is named, not just the verb. Several aircraft can hold
    // targets at once and an unattributed marker makes the operator guess.
    title: `${kindLabel(kind)} · ${callsignFor(droneId)}`,
    lines,
    color: typeof properties.color === 'string' ? properties.color : trackColorFor(droneId),
  };
}

export interface CommandTargetLayerOptions {
  /** Called with the hovered target, and with null when the pointer leaves it. */
  onHover?: (hover: CommandTargetHover | null) => void;
}

export interface CommandTargetLayer {
  destroy: () => void;
  /** Redraws applied, and how many targets are currently drawn. */
  stats: () => { redraws: number; targets: number };
}

export function createCommandTargetLayer(
  map: MapLike,
  options: CommandTargetLayerOptions = {},
): CommandTargetLayer {
  let redraws = 0;
  let drawn = 0;
  let latest: CommandTarget[] = [];

  if (!map.getSource(COMMAND_TARGET_SOURCE_ID)) {
    map.addSource(COMMAND_TARGET_SOURCE_ID, { type: 'geojson', data: EMPTY });
  }

  if (!map.getLayer(COMMAND_TARGET_RING_LAYER)) {
    map.addLayer({
      id: COMMAND_TARGET_RING_LAYER,
      type: 'line',
      source: COMMAND_TARGET_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'LineString'],
      // Dashed and unfilled: a zone is a filled polygon and a threat ring is
      // red or amber, so an orbit reads as neither.
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.6,
        'line-opacity': 0.9,
        'line-dasharray': [2, 2],
      },
    });
  }

  if (!map.getLayer(COMMAND_TARGET_HALO_LAYER)) {
    map.addLayer({
      id: COMMAND_TARGET_HALO_LAYER,
      type: 'circle',
      source: COMMAND_TARGET_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      // Hollow, and wider than any mission waypoint dot. The darkened middle
      // keeps the core legible over bright imagery without hiding the ground.
      paint: {
        'circle-radius': 11,
        'circle-color': 'rgba(0,0,0,0.28)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });
  }

  if (!map.getLayer(COMMAND_TARGET_CORE_LAYER)) {
    map.addLayer({
      id: COMMAND_TARGET_CORE_LAYER,
      type: 'circle',
      source: COMMAND_TARGET_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 3, 'circle-color': ['get', 'color'] },
    });
  }

  if (!map.getLayer(COMMAND_TARGET_LABEL_LAYER)) {
    map.addLayer({
      id: COMMAND_TARGET_LABEL_LAYER,
      type: 'symbol',
      source: COMMAND_TARGET_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-letter-spacing': 0.08,
        'text-allow-overlap': true,
        // Below the marker. Waypoint sequence numbers sit above theirs, so the
        // two never stack into one unreadable smear at a shared coordinate.
        'text-offset': [0, 1.6],
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  function present(targets: CommandTarget[]): CommandTarget[] {
    const roster = new Set(useDroneStore.getState().ids);
    return targets.filter((target) => roster.has(target.droneId));
  }

  function redraw(): void {
    const source = geoJsonSource(map, COMMAND_TARGET_SOURCE_ID);
    if (!source) return;
    const targets = present(latest);
    source.setData({
      type: 'FeatureCollection',
      features: targets.flatMap(commandTargetFeatures),
    });
    drawn = targets.length;
    redraws += 1;
  }

  const unsubscribe = subscribeToCommandTargets((targets) => {
    latest = targets;
    redraw();
  });
  // Redraw on roster changes so a target whose aircraft has just despawned
  // stops being drawn immediately.
  const unsubscribeRoster = subscribeToRoster(() => redraw());

  const onHoverMove = (event: HoverEvent): void => {
    const properties = event.features?.[0]?.properties;
    if (!properties) return;
    options.onHover?.(
      hoverFromProperties(properties, event.point, (droneId) =>
        useDroneStore.getState().drones.get(droneId)?.callsign ?? droneId,
      ),
    );
  };
  const onHoverLeave = (): void => options.onHover?.(null);

  const unbindHover = bindHoverLayer(map, COMMAND_TARGET_HALO_LAYER, onHoverMove, onHoverLeave);

  return {
    destroy: () => {
      unsubscribe();
      unsubscribeRoster();
      unbindHover();
      // A label left behind would hang over a map that no longer has the layer.
      options.onHover?.(null);
      for (const id of [
        COMMAND_TARGET_LABEL_LAYER,
        COMMAND_TARGET_CORE_LAYER,
        COMMAND_TARGET_HALO_LAYER,
        COMMAND_TARGET_RING_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(COMMAND_TARGET_SOURCE_ID)) map.removeSource(COMMAND_TARGET_SOURCE_ID);
    },
    stats: () => ({ redraws, targets: drawn }),
  };
}

/**
 * The hovered target, published outside React.
 *
 * Two constraints meet here. The layer has to be created by the screen itself
 * rather than by a child component, because React runs child effects first and
 * a layer added early sits underneath every layer added later — mounted from a
 * child, a target would be drawn under the mission routes and zone fills. And
 * the label has to re-render on its own, because a pointer sliding across a
 * marker must not re-render the command screen's rails and dock at pointer
 * rate. So the hook publishes and the label subscribes.
 */
const hoverListeners = new Set<(hover: CommandTargetHover | null) => void>();

function publishHover(hover: CommandTargetHover | null): void {
  for (const listener of hoverListeners) listener(hover);
}

/** Mount the layer. Call it from the screen, alongside the other layer hooks. */
export function useCommandTargetLayer(): void {
  const controller = useMapController();

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createCommandTargetLayer(map, {
          onHover: (hover) => {
            // The pointer cursor is the affordance that says the marker answers
            // to the pointer, matching the draft waypoints on Planning.
            const raw = controller.raw();
            if (raw) raw.getCanvas().style.cursor = hover ? 'pointer' : '';
            publishHover(hover);
          },
        });
        return () => layer.destroy();
      }),
    [controller],
  );
}

/** Roughly the label's width, for keeping it inside the viewport. */
const LABEL_WIDTH = 190;

/** The hover label for whatever target the pointer is over. */
export function CommandTargetHoverLabel(): ReactNode {
  const [hover, setHover] = useState<CommandTargetHover | null>(null);

  useEffect(() => {
    hoverListeners.add(setHover);
    return () => {
      hoverListeners.delete(setHover);
    };
  }, []);

  if (!hover) return null;

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const left = Math.min(hover.x + 14, viewportWidth - LABEL_WIDTH - 12);

  return (
    <div
      role="tooltip"
      className="glass pointer-events-none absolute z-40 rounded-[12px] px-3 py-2"
      style={{ left, top: hover.y + 14, maxWidth: LABEL_WIDTH }}
    >
      <span
        className="block font-mono text-[11px] font-semibold tracking-wide"
        style={{ color: hover.color }}
      >
        {hover.title}
      </span>
      {hover.lines.map((line) => (
        <span key={line} className="mt-0.5 block font-mono text-[9.5px] text-white/55">
          {line}
        </span>
      ))}
    </div>
  );
}

export default CommandTargetHoverLabel;
